import { useEffect } from "react";

/**
 * Per-page SEO. Mounted at the top of each route to update document.title
 * and the relevant meta tags (description, OG, Twitter Card, canonical).
 *
 * Pas de dépendance externe : on manipule directement les balises du
 * <head> via le DOM. Les balises par défaut sont déjà posées dans
 * `public/index.html` ; on se contente de surcharger leur contenu.
 */

export const SITE_ORIGIN = "https://aoria.org";
const DEFAULT_IMAGE = "/og-image.png";

function setMeta(attr, name, content) {
    if (!content) return;
    const selector = `meta[${attr}="${name}"]`;
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function setCanonical(href) {
    if (!href) return;
    let el = document.head.querySelector('link[rel="canonical"]');
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", "canonical");
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

export default function Seo({
    title,
    description,
    path = "/",
    type = "website",
    image = DEFAULT_IMAGE,
    publishedAt,
    locale = "fr_FR",
}) {
    useEffect(() => {
        if (title) document.title = title;

        if (description) {
            setMeta("name", "description", description);
            setMeta("property", "og:description", description);
            setMeta("name", "twitter:description", description);
        }

        if (title) {
            setMeta("property", "og:title", title);
            setMeta("name", "twitter:title", title);
        }

        const url = SITE_ORIGIN + path;
        setMeta("property", "og:url", url);
        setMeta("property", "og:type", type);
        setMeta("property", "og:locale", locale);
        setMeta("property", "og:site_name", "Aoria");
        setCanonical(url);

        const imageUrl = image.startsWith("http")
            ? image
            : SITE_ORIGIN + image;
        setMeta("property", "og:image", imageUrl);
        setMeta("property", "og:image:width", "1200");
        setMeta("property", "og:image:height", "630");
        setMeta("name", "twitter:image", imageUrl);
        setMeta("name", "twitter:card", "summary_large_image");

        if (publishedAt) {
            setMeta("property", "article:published_time", publishedAt);
        }
    }, [title, description, path, type, image, publishedAt, locale]);

    return null;
}
